import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button, buttonVariants } from '../../components/ui/button';
import { type Columns, DataTable } from '../../components/ui/data-table';
import { Checkbox, Input, Select } from '../../components/ui/input';
import { api, errorMessage } from '../../lib/api';
import { brandsQuery, categoriesQuery, categoryLabel } from '../../lib/queries';
import { useCan } from '../../lib/session';
import type { ProductListItem, ProductStatus } from '../../lib/types';
import { formatDateTime } from '../../lib/utils';

export const STATUS_BADGE = {
  draft: 'secondary',
  active: 'success',
  archived: 'outline',
} as const satisfies Record<ProductStatus, string>;

export function ProductListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canWrite = useCan('catalog.write');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<ProductStatus | ''>('');
  const [categoryId, setCategoryId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [noPackage, setNoPackage] = useState(false);
  const categories = useQuery(categoriesQuery);
  const brands = useQuery(brandsQuery);

  const filters = { q: q.trim(), status, categoryId, brandId, noPackage: noPackage ? 'true' : '' };
  const products = useInfiniteQuery({
    queryKey: ['catalog', 'products', filters],
    queryFn: ({ pageParam }) =>
      api<{ items: ProductListItem[]; nextAfter: string | null }>('/catalog/products', {
        query: { ...filters, limit: 50, after: pageParam },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextAfter ?? undefined,
  });
  const rows = useMemo(() => products.data?.pages.flatMap((p) => p.items) ?? [], [products.data]);

  const columns = useMemo<Columns<ProductListItem>>(
    () => [
      {
        id: 'name',
        header: t('common.name'),
        accessorFn: (row) => row.name ?? '—',
        cell: ({ row }) => (
          <span className="font-medium">
            {/* Настоящая ссылка: открывается с клавиатуры и в новой вкладке. */}
            <Link
              to="/products/$productId"
              params={{ productId: row.original.id }}
              className="hover:underline"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {row.original.name ?? '—'}
            </Link>
            {row.original.isDemo ? (
              <Badge variant="outline" className="ml-2">
                {t('common.demo')}
              </Badge>
            ) : null}
            {row.original.noPackage ? (
              <Badge variant="warning" className="ml-2" title={t('products.noPackageHint')}>
                {t('products.noPackage')}
              </Badge>
            ) : null}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('common.status'),
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <Badge variant={STATUS_BADGE[row.original.status]}>
            {t(`products.status.${row.original.status}`)}
          </Badge>
        ),
      },
      { id: 'variants', header: t('products.variants'), accessorFn: (row) => row.variantCount },
      {
        id: 'updated',
        header: t('products.updated'),
        accessorFn: (row) => row.updatedAt,
        cell: ({ row }) => formatDateTime(row.original.updatedAt, i18n.language),
      },
    ],
    [t, i18n.language],
  );

  return (
    <>
      <PageHeader
        title={t('products.title')}
        actions={
          canWrite ? (
            <Link to="/products/new" className={buttonVariants()}>
              <Plus />
              {t('products.new')}
            </Link>
          ) : null
        }
      />
      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <Input
          type="search"
          aria-label={t('common.search')}
          placeholder={t('products.searchPlaceholder')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
          }}
        />
        <Select
          aria-label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ProductStatus | '');
          }}
        >
          <option value="">{t('common.all')}</option>
          {(['draft', 'active', 'archived'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`products.status.${s}`)}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t('products.primaryCategory')}
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
          }}
        >
          <option value="">{t('common.all')}</option>
          {(categories.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {categoryLabel(c, categories.data ?? [], i18n.language)}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t('products.brand')}
          value={brandId}
          onChange={(e) => {
            setBrandId(e.target.value);
          }}
        >
          <option value="">{t('common.all')}</option>
          {(brands.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </Select>
      </div>
      <label className="mb-4 flex items-center gap-2 text-sm">
        <Checkbox
          checked={noPackage}
          onChange={(e) => {
            setNoPackage(e.target.checked);
          }}
        />
        {t('products.onlyNoPackage')}
      </label>
      {products.error ? (
        <Alert variant="destructive" className="mb-4">
          {errorMessage(products.error)}
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={rows}
        loading={products.isPending}
        rowKey={(row) => row.id}
        onRowClick={(row) => {
          void navigate({ to: '/products/$productId', params: { productId: row.id } });
        }}
      />
      {products.hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            disabled={products.isFetchingNextPage}
            onClick={() => void products.fetchNextPage()}
          >
            {t('common.loadMore')}
          </Button>
        </div>
      ) : null}
    </>
  );
}
